"use client";

import { useEffect, useRef, useState, type ReactNode } from "react";

interface FilterDropdownProps {
  label: string;
  /** Replaces the label once a selection is made. */
  selectedLabel?: string | null;
  icon?: ReactNode;
  /**
   * Mobile collapses the trigger to a 40px icon tile, as drawn, and the label
   * comes back from `lg:` up.
   */
  compact?: boolean;
  /**
   * When set, the trigger opens this instead of the inline menu below `lg`.
   * The sticky filter bar is its own stacking context, so the sheet itself has
   * to be rendered by the page to clear the site header.
   */
  onOpenMobileSheet?: () => void;
  children: (close: () => void) => ReactNode;
  className?: string;
}

/**
 * The dropdown shell shared by Collections, Categories and Sort by. Owns the
 * open state, click-outside and Escape handling so each menu only supplies its
 * options.
 */
export default function FilterDropdown({
  label,
  selectedLabel,
  icon,
  compact = false,
  onOpenMobileSheet,
  children,
  className = "",
}: FilterDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    const onPointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setIsOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsOpen(false);
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen]);

  const isSelected = Boolean(selectedLabel);

  // Which surface the trigger opens is a breakpoint decision, and both can't be
  // mounted at once without duplicating the control for screen readers.
  const toggle = () => {
    if (onOpenMobileSheet && !window.matchMedia("(min-width: 1024px)").matches) {
      onOpenMobileSheet();
      return;
    }
    setIsOpen(open => !open);
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={toggle}
        aria-expanded={isOpen}
        aria-haspopup="true"
        // The label is hidden while compact, so it has to be named either way.
        aria-label={selectedLabel || label}
        className={`flex items-center w-full min-h-10 lg:min-h-11 gap-2 font-roboto text-sm font-medium leading-5 bg-white rounded border transition-colors ${
          compact ? "justify-center lg:justify-start px-0 lg:px-2.5" : "justify-start px-2.5"
        } ${isSelected ? "text-primary border-primary" : "text-gray-1 border-transparent hover:border-gray-8"}`}>
        {icon}
        <span className={`truncate ${compact ? "hidden lg:inline" : ""}`}>{selectedLabel || label}</span>
      </button>

      {isOpen && (
        // A compact trigger sits near the right edge, so the menu hangs off its
        // right rather than overflowing the screen.
        <div
          className={`${onOpenMobileSheet ? "hidden lg:flex" : "flex"} flex-col absolute top-[calc(100%+4px)] z-30 min-w-full p-[5px] bg-white border border-black/8 rounded-[10px] shadow-[0_1.5px_8px_0_rgba(0,0,0,0.2)] overflow-hidden ${
            compact ? "right-0 lg:right-auto lg:left-0" : "left-0"
          }`}>
          <div className="flex flex-col w-full max-h-[320px] overflow-y-auto">{children(() => setIsOpen(false))}</div>
        </div>
      )}
    </div>
  );
}
